import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

const faqItems = [
  {
    question: "Why does the cost vary between patients?",
    answer: "Each patient\\'s medical condition is unique. Factors such as overall health, kidney function, complications, and specific treatment requirements affect the final cost determined by the treating hospital."
  },
  {
    question: "Are payments made to you or to the hospital?",
    answer: "Medical payments are handled directly with the hospital unless otherwise disclosed. We provide coordination support, and payment arrangements are clearly explained during the consultation process."
  },
  {
    question: "What is included in the estimated cost range?",
    answer: "The estimated range typically includes pre-transplant evaluation, surgery, hospitalization, medical coordination, immunosuppressive medications during hospital stay, and initial post-operative care. Final pricing is determined by the treating hospital after clinical evaluation."
  },
  {
    question: "Are there any hidden fees?",
    answer: "No hidden coordination fees. We provide transparent cost guidance. All costs are explained clearly before proceeding. Travel, accommodation, and long-term medications are separate expenses that are disclosed upfront."
  },
  {
    question: "Can I pay in installments?",
    answer: "Most programs use staged payments — an initial deposit, a pre-surgery payment, and a final payment. The exact schedule depends on the hospital and country and is explained in advance."
  },
  {
    question: "How do I get a personalized cost estimate?",
    answer: "Contact us for a free consultation. After reviewing your medical information, we provide general cost guidance based on your specific situation. Final medical pricing is determined exclusively by the treating hospital after clinical evaluation."
  }
];

export default function CostPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Cost of Kidney Transplant Abroad | Transparent Pricing & Comparison';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', 'Transparent cost breakdown for kidney transplant abroad. Estimated program range USD 120,000-150,000. Direct hospital coordination, no hidden fees. Compare international kidney transplant costs.');
    updateMetaTag('keywords', 'cost of kidney transplant abroad, kidney transplant cost, international transplant pricing, transplant cost comparison, affordable kidney transplant, hospital direct pricing');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    updateLinkTag('canonical', 'https://kidneypatientsupport.com/cost');

    // Hreflang tags
    updateHreflangTags({
      enPath: '/cost',
      jaPath: '/cost' // Japanese cost page exists
    });

    updateMetaTag('og:url', 'https://kidneypatientsupport.com/cost', true);
    updateMetaTag('og:title', 'Cost of Kidney Transplant Abroad | Transparent Pricing Overview', true);
    updateMetaTag('og:description', 'Transparent cost breakdown for kidney transplant abroad. Estimated program range clearly labeled. Direct hospital coordination with no hidden fees.', true);
    updateMetaTag('og:type', 'website', true);

    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/cost');
    updateMetaTag('twitter:title', 'Cost of Kidney Transplant Abroad | Transparent Pricing Overview');
    updateMetaTag('twitter:description', 'Transparent cost breakdown for kidney transplant abroad. Estimated program range clearly labeled. Direct hospital coordination with no hidden fees.');
    updateMetaTag('twitter:card', 'summary_large_image');

    const scripts: HTMLScriptElement[] = [];

    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cost of Kidney Transplant Abroad",
      "description": "Transparent cost breakdown for kidney transplant abroad with direct hospital coordination.",
      "url": "https://kidneypatientsupport.com/cost",
      "@id": "https://kidneypatientsupport.com/cost",
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://kidneypatientsupport.com/#website"
      },
      "publisher": {
        "@id": "https://kidneypatientsupport.com/#organization"
      }
    });
    document.head.appendChild(webPageScript);
    scripts.push(webPageScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": { "@type": "Answer", "text": item.answer }
      }))
    });
    document.head.appendChild(faqScript);
    scripts.push(faqScript);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scripts.forEach(script => {
        if (script.parentNode) document.head.removeChild(script);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/ethics" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Ethics</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
            <Link to="/countries" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Countries</Link>
            <Link to="/pricing" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Pricing</Link>
            <Link to="/cost" className="text-sm font-medium text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Cost</Link>
            <Link to="/process" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Process</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/complete-guide-kidney-transplant-abroad" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Guide</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/cost" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Cost</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 pt-32 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <div className="inline-block bg-teal-100 text-teal-700 px-6 py-3 rounded-full mb-8">
            <span className="text-sm font-bold tracking-wide">TRANSPARENT COST BREAKDOWN</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
            Cost of Kidney Transplant Abroad
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
            A clear, honest overview of <strong>international kidney transplant costs</strong> — what&apos;s included, what&apos;s not, and why our direct coordination model keeps costs lower than multi-layer agencies.
          </p>
          <p className="text-gray-600 text-base leading-relaxed max-w-3xl mx-auto mb-10">
            For patients considering <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> or other destinations. Review the <Link to="/process" className="text-teal-600 hover:text-teal-700 font-medium">transplant process</Link> alongside cost planning.
          </p>
          <div className="bg-white rounded-3xl p-10 shadow-2xl border-4 border-teal-600 mb-8 inline-block">
            <p className="text-gray-600 text-lg mb-4">Estimated program range:</p>
            <div className="text-6xl font-bold text-teal-600 mb-2">USD 120,000–150,000</div>
            <p className="text-gray-600 text-base mb-4"><em>(Estimated)</em></p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mt-6">
              <p className="text-gray-700 text-sm leading-relaxed text-left">
                <strong>Important:</strong> Final medical pricing is determined by the treating hospital after clinical evaluation. Individual medical factors may affect final cost.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center bg-teal-600 text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap shadow-xl cursor-pointer">
              <span>Request Cost Information</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-teal-50 transition-colors space-x-3 whitespace-nowrap cursor-pointer">
              <span>View Pricing Details</span>
              <i className="ri-file-list-3-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included / Not Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What&apos;s Included in the <span className="text-teal-600">Transplant Cost</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Full transparency on what the <strong>kidney transplant cost abroad</strong> covers — and what it doesn&apos;t.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-10 border-2 border-teal-200">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-checkbox-circle-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Included</h3>
              <ul className="space-y-4">
                {[
                  'Pre-transplant medical evaluation',
                  'Kidney transplant surgery',
                  'Hospitalization & inpatient care',
                  'Medical coordination with the hospital',
                  'Immunosuppressive medications (during stay)',
                  'Initial post-operative care',
                  'Official medical records'
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-gray-700 text-base">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                    <span><strong>{item}</strong></span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-10 border-2 border-orange-200">
              <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-close-circle-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Not Included</h3>
              <p className="text-gray-700 text-base mb-6">These are separate expenses, disclosed upfront:</p>
              <ul className="space-y-4">
                {[
                  'International airfare',
                  'Accommodation outside the hospital',
                  'Visa & travel-related fees',
                  'Travel insurance',
                  'Expenses for accompanying family',
                  'Long-term medications after discharge'
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-gray-700 text-base">
                    <i className="ri-close-line text-orange-600 mt-1 mr-3 flex-shrink-0 text-xl font-bold"></i>
                    <span><strong>{item}</strong></span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 bg-white rounded-2xl p-4 border border-orange-200">
                <p className="text-gray-700 text-sm">
                  These costs vary by country and personal situation and are explained during consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Cost Is Lower */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Our <span className="text-teal-600">Kidney Transplant Cost</span> Is Lower
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Many overseas providers charge <strong>USD 200,000+</strong>. The difference isn&apos;t medical quality — it&apos;s the elimination of multiple intermediary layers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-error-warning-line text-orange-600 mr-3 text-2xl"></i>
                Traditional Multi-Layer Model
              </h3>
              <div className="space-y-3">
                {['Patient', 'Domestic broker (+Commission)', 'Overseas agent (+Commission)', 'Local coordinator (+Commission)', 'Hospital'].map((step, i) => (
                  <div key={i}>
                    <div className="flex items-center">
                      <div className={`w-10 h-10 ${i === 0 || i === 4 ? 'bg-gray-200' : 'bg-orange-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <i className={`${i === 0 ? 'ri-user-line text-gray-700' : i === 4 ? 'ri-hospital-line text-gray-700' : 'ri-building-line text-orange-600'} text-lg`}></i>
                      </div>
                      <p className="ml-4 text-gray-900 font-semibold text-sm">{step}</p>
                    </div>
                    {i < 4 && (
                      <div className="ml-5 border-l-3 border-orange-300 h-4"></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-orange-50 rounded-2xl p-4 border border-orange-200">
                <p className="text-orange-700 text-sm font-semibold">Result: USD 200,000+ due to stacked commissions</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-teal-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-checkbox-circle-fill text-teal-600 mr-3 text-2xl"></i>
                Our Direct Coordination Model
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { icon: 'ri-hospital-line', title: 'Direct hospital coordination' },
                  { icon: 'ri-link-unlink', title: 'No unnecessary middlemen' },
                  { icon: 'ri-close-circle-line', title: 'No hidden commissions' },
                  { icon: 'ri-file-list-3-line', title: 'Transparent itemization' }
                ].map((item, i) => (
                  <div key={i} className="bg-teal-50 rounded-2xl p-5 border border-teal-200">
                    <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-white text-lg`}></i>
                    </div>
                    <p className="text-gray-900 font-bold text-sm">{item.title}</p>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-4 text-center">
                <p className="text-white font-bold">Result: USD 120,000–150,000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kidney Transplant Cost <span className="text-teal-600">Comparison</span>
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-teal-600 to-teal-700">
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Provider Type</th>
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Typical Cost</th>
                  <th className="px-8 py-6 text-left text-white font-bold text-lg">Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-8 py-6 font-semibold text-gray-900">Multi-layer agencies</td>
                  <td className="px-8 py-6 text-orange-600 font-bold">USD 200,000+</td>
                  <td className="px-8 py-6 text-gray-700">Multiple intermediaries add commissions</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-8 py-6 font-semibold text-gray-900">US domestic (average)</td>
                  <td className="px-8 py-6 text-orange-600 font-bold">USD 300,000–400,000+</td>
                  <td className="px-8 py-6 text-gray-700">Hospital charges, insurance gaps</td>
                </tr>
                <tr className="bg-teal-50">
                  <td className="px-8 py-6 font-semibold text-gray-900">Hospital-direct coordination (us)</td>
                  <td className="px-8 py-6 text-teal-600 font-bold text-xl">USD 120,000–150,000</td>
                  <td className="px-8 py-6 text-gray-700">Direct structure, no middlemen</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">Figures shown are for medical costs only. Travel and accommodation are separate.</p>
          </div>
        </div>
      </section>

      {/* Country-Specific Costs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Cost by <span className="text-teal-600">Destination Country</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              While the total <strong>kidney transplant cost</strong> range is similar across <Link to="/countries" className="text-teal-600 hover:text-teal-700 font-medium">our partner countries</Link>, specific details may vary:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/india" className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">India</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Comprehensive evaluation and experienced transplant teams at established medical centers.</p>
            </Link>

            <Link to="/cambodia" className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-time-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Cambodia</h3>
              <p className="text-gray-700 leading-relaxed text-sm">In some cases, shorter timelines depending on eligibility and medical assessment.</p>
            </Link>

            <Link to="/pakistan" className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-wallet-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Pakistan</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Considered when balancing cost, timing, and access to experienced transplant surgeons.</p>
            </Link>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-base">Each option is explained clearly based on your medical condition and eligibility.</p>
          </div>
        </div>
      </section>

      {/* Payment Structure */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Payment Structure: <span className="text-teal-600">Staged Options</span>
            </h2>
          </div>

          <div className="bg-gray-50 rounded-3xl p-10 border border-gray-200 mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Most programs use <strong>staged payments</strong> to help manage the kidney transplant cost:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Initial Deposit', desc: 'Secures your place in the program' },
                { step: '2', title: 'Pre-Surgery Payment', desc: 'Before the transplant procedure' },
                { step: '3', title: 'Final Payment', desc: 'Upon completion of treatment' }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-lg">{item.step}</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-200">
              <p className="text-gray-700 text-base">
                The exact schedule depends on the hospital and country and is explained in advance. No surprise charges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-yellow-50 border-y border-yellow-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-10 border-2 border-yellow-200">
            <div className="flex items-start">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-yellow-600 text-2xl"></i>
              </div>
              <div className="ml-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Cost Disclaimer</h3>
                <ul className="space-y-3">
                  {[
                    'Final medical pricing is determined by the treating hospital after clinical evaluation.',
                    'This page provides cost guidance only — not a binding quote.',
                    'No hidden coordination fees.',
                    'Individual medical factors may affect final cost.'
                  ].map((text, i) => (
                    <li key={i} className="flex items-start text-gray-700 text-base">
                      <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span><strong>{text}</strong></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kidney Transplant Cost: <span className="text-teal-600">FAQ</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className={`bg-gray-50 rounded-2xl border transition-all ${openFaqIndex === index ? 'border-teal-300 shadow-md' : 'border-gray-200'}`}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-100 transition-colors rounded-2xl"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">Q: {item.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-xl`}></i>
                  </div>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 text-base leading-relaxed">
                        <strong>A:</strong> {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Questions About Transplant Costs?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-3xl mx-auto">
            Every patient&apos;s situation is different. Contact us for personalized <strong>kidney transplant cost</strong> guidance. Final pricing is determined by the treating hospital.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 px-12 py-6 rounded-full text-xl font-bold hover:bg-gray-100 transition-colors space-x-3 whitespace-nowrap shadow-2xl cursor-pointer">
            <span>Request Information</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Micro CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-700 text-xl mb-6">
            Unsure if this is right for you?
          </p>
          <Link
            to="/who-this-is-for"
            className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-50 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
          >
            <span>Check Eligibility</span>
            <i className="ri-checkbox-circle-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-white/90 text-base leading-relaxed max-w-4xl mx-auto">
              <strong>This website provides coordination and informational support services only.</strong><br />
              Medical decisions and procedures are handled exclusively by licensed healthcare providers.
            </p>
          </div>

          <div className="border-t border-white/20 pt-8 mb-8">
            <h4 className="text-white font-bold text-sm tracking-wide mb-4 text-center">CORE PAGES</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/pakistan" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Kidney Transplant in Pakistan</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Pricing</Link>
              <Link to="/cost" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Cost Overview</Link>
              <Link to="/process" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">How the Process Works</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethical Framework</Link>
              <Link to="/who-this-is-for" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Eligibility Guide</Link>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/80 text-sm mb-4 md:mb-0">
              &copy; 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
